import {
  FC, useEffect, useMemo, 
} from 'react';
import { useTranslation } from 'react-i18next';

import { FieldProps } from 'features/Croptimizer/types';
import {
  Country, CropProps, CropTableProps, TypeOfKey, 
} from 'utils/types';
import { unMaskInput, unMaskInputValue } from 'utils/helpers';
import useCroptimizerCrop from 'api/hooks/useCroptimizerCrop';
import { cn } from 'utils/cn';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import { GermanFieldComponent } from './screens/German/GermanFieldComponent';
import FieldComponent from './screens/Standart/FieldComponent';
import { TabBar } from './components/TabBar';
import { useAddField } from './hooks/useAddField';
import { useGenerateCrop } from './hooks/useGenerateCrop';
import { getOpenFields } from './utils';

interface Props {
  isPDF?: boolean;
}

const CropRotation: FC<Props> = ({ isPDF }) => {
  const { t } = useTranslation();
  const {
    user,
    setFields,
    setActiveTabCrop,
    fields,
    crops,
    setCrops,
    boardSoilValues,
    boardStatusValues,
    boardCropValues,
    mineralizationValues,
  } = useCroptimizer();
  const isGermany = user?.country?.name === Country.Germany;

  const { data: cropList } = useCroptimizerCrop();
  const { generateCrop } = useGenerateCrop();

  const currentCrop = useMemo(() => {
    let currentCropLocal = cropList?.[0];

    if (boardCropValues?.crop) {
      currentCropLocal = cropList?.find(
        (item) => item.Crops === boardCropValues.crop,
      );

      if (currentCropLocal) {
        currentCropLocal = {
          ...currentCropLocal,
          PotentialYeild: +unMaskInput(boardCropValues.currentYield),
          Price: +unMaskInput(boardCropValues.price),
        };
      }
    }

    return currentCropLocal as CropProps;
  }, [cropList, boardCropValues]);

  const { addField } = useAddField(currentCrop);

  const onDelete = (id: number) => {
    const getIndexof = fields.findIndex((field) => field.id === id);
    const filteredData = fields.filter((field) => field.id !== id);
    const notFilteredData = fields.filter((field) => field.id === id);
    const filteredCrops = crops.filter(
      (crop) => crop.fieldName !== notFilteredData?.[0]?.title,
    );
    setCrops(filteredCrops);
    setFields(filteredData);
    setActiveTabCrop(
      fields?.[getIndexof - 1]?.id ?? fields?.[getIndexof + 1]?.id,
    );
  };

  const setCropTableToFieldObject =
    (id: number) => (
      tableDataOrUpdater:
      | CropTableProps[]
      | ((prev: CropTableProps[]) => CropTableProps[]),
    ) => {
      setFields(
        (prevFields: FieldProps[]) => prevFields.map((field) => {
          if (field.id === id) {
            const currentTableData = field.object?.tableDataI || [];
            const newTableData =
                typeof tableDataOrUpdater === 'function'
                  ? tableDataOrUpdater(currentTableData)
                  : tableDataOrUpdater;

            return {
              ...field,
              object: {
                ...field.object,
                tableDataI: newTableData,
              },
            };
          }

          return field;
        }) as FieldProps[],
      );
    };

  const setFieldObjectField =
    (id: number) => (name: keyof Required<FieldProps>['object']) => (value: TypeOfKey<Required<FieldProps>['object']>) => {
      setFields(
        (prevFields: FieldProps[]) => prevFields.map((field) => {
          if (field.id === id) {
            return {
              ...field,
              object: {
                ...field.object,
                [name]: value,
              },
            };
          }

          return field;
        }) as FieldProps[],
      );
    };

  useEffect(() => {
    if (
      boardCropValues?.yieldGain &&
      mineralizationValues?.annualDecrease &&
      currentCrop
    ) {
      if (!fields.length) {
        addField(true)();

        return;
      }

      const openFields = getOpenFields(fields, crops);

      setFields(
        (prevFields: FieldProps[]) => prevFields.map((field) => {
          if (openFields.some((item) => item.id === field.id)) {
            const fieldExist =
                field.object && field.object?.tableDataI?.length;
            const fieldObjectTableData = field.object?.tableDataI || [];

            const generateTableDataI = () => {
              if (fieldExist) {
                return fieldObjectTableData.map((data, index) => {
                  if (
                    data.name === boardCropValues.crop &&
                      currentCrop &&
                      !data.n
                  ) {
                    const newCrop = generateCrop(
                      currentCrop,
                      field.object,
                      data.id,
                      undefined,
                      index,
                    );

                    return newCrop;
                  }

                  return data;
                });
              }

              const newCrop = currentCrop
                ? [generateCrop(currentCrop, field.object)]
                : [];

              return newCrop;
            };

            const newField = {
              ...field,
              object: {
                ...(field.object || {}),
                tableDataI: generateTableDataI(),
              },
            };

            return newField;
          }

          return field;
        }) as FieldProps[],
      );
    }
  }, [boardCropValues, currentCrop, mineralizationValues]);

  useEffect(() => {
    if (boardSoilValues) {
      const openFields = getOpenFields(fields, crops);

      setFields((prevFields: FieldProps[]) => prevFields.map((field) => {
        if (openFields.some((item) => item.id === field.id)) {
          const newField = {
            ...field,
            object: {
              ...(field.object || {}),
              soilTypeI:
                  field?.object?.soilTypeI ?? boardSoilValues?.soilType ?? '',
              secI: field?.object?.secI ?? boardSoilValues?.secsmol ?? '',
              productTypeI:
                  field?.object?.productTypeI ??
                  boardSoilValues?.productName ??
                  '',
              nvI: field?.object?.nvI ?? boardSoilValues?.nv ?? '',
              mtI: field?.object?.mtI ?? boardSoilValues?.mt ?? '',
            },
          } as FieldProps;

          return newField;
        }

        return field;
      }),
      );
    }
  }, [boardSoilValues]);

  useEffect(() => {
    if (boardStatusValues) {
      const openFields = getOpenFields(fields, crops);

      setFields((prevFields: FieldProps[]) => prevFields.map((field) => {
        if (openFields.some((item) => item.id === field.id)) {
          const newField = {
            ...field,
            object: {
              ...(field.object || {}),
              currentphI:
                  field?.object?.currentphI ??
                  unMaskInputValue(boardStatusValues?.currentPh) ??
                  '5,8',
              currentSecI:
                  field?.object?.currentSecI ??
                  boardStatusValues?.currentSec ??
                  '',
              targetphI:
                  field?.object?.targetphI ??
                  unMaskInputValue(boardStatusValues?.targetPh) ??
                  '6,3',
              targetSecI:
                  field?.object?.targetSecI ??
                  boardStatusValues?.targetSec ??
                  '',
            },
          } as FieldProps;

          return newField;
        }

        return field;
      }),
      );
    }
  }, [boardStatusValues]);

  const renderContent = (field: FieldProps) => {
    const sharedProps = {
      id: field.id,
      object: field?.object,
      onDelete: () => onDelete(field.id),
      cropList: cropList,
      currentCrop: currentCrop,
      setCropTableToFieldObject: setCropTableToFieldObject(field.id),
      setFieldObjectField: setFieldObjectField(field.id),
      isPDF: isPDF,
    };

    switch (isGermany) {
      case true:
        return <GermanFieldComponent {...sharedProps} />;
      default:
        return <FieldComponent {...sharedProps} />;
    }
  };

  return (
    <div
      className={cn(
        'mb-6 flex flex-col border border-primitiveGrey rounded-2xl p-8',
        { 'md:p-4': !isPDF },
      )}
    >
      <div className="text-black font-bold mb-8 md:mb-6 text-xl md:text-lg">
        {t('cropRotation')}
      </div>
      <TabBar isPDF={isPDF} addField={addField} />

      <div>
        {fields.map((field) => (
          <div key={field.id}>{renderContent(field)}</div>
        ))}
      </div>
    </div>
  );
};

export default CropRotation;
